import { pathOr } from 'ramda';
import { createAction } from 'redux-actions';

import { resetForm } from '@gef-ui/form-core/stream/transfer/control';

import { appendMode } from 'constants/general';
import appContext from 'features/common/appContext';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { subheaders } from 'features/common/Subheader/constants';
import { setVisibleState } from 'features/common/Subheader/slice';
import user from 'features/views/user';
import { generateCommonFetchActions, trimFormData } from 'utils';

import * as types from './actionTypes';
import { campaignViews, formDataFieldsMap } from './constants';
import { CURRENT_CAMPAIGNS_FORM_NAME } from './form';
import { getFullSort, getSort } from './selectors';
import { queryParamsFactory } from './utils';

const { CURRENT_CAMPAIGNS, PLANED_CAMPAIGNS } = campaignViews;

export const appendCampaigns = createAction(types.APPEND_CAMPAIGNS, (type, campaigns) => ({ type, campaigns }));
export const reset = createAction(types.RESET);
export const setCampaigns = createAction(types.SET_CAMPAIGNS, (type, campaigns) => ({ type, campaigns }));
export const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));

const currentCampaignsUrl = `${fetch.constants.urls.campaignList}.${CURRENT_CAMPAIGNS}`;
const planedCampaignsUrl = `${fetch.constants.urls.campaignListSettings}.${PLANED_CAMPAIGNS}`;

const fetchCampaigns =
	(type) =>
	({ queryParams, callGtmFilter, formData }, append) =>
	(dispatch, getState) => {
		const sort = getFullSort(getState());
		const channelIds = user.selectors.getChannelIds(getState());
		const params = queryParams || queryParamsFactory(type, {}, sort, channelIds);
		const role = user.selectors.getCurrentRole(getState());
		const fetchUrl =
			type === CURRENT_CAMPAIGNS ? fetch.constants.urls.campaignList : fetch.constants.urls.campaignListSettings;

		dispatch(
			fetch.actions.get(fetchUrl, {
				takeLatest: true,
				uniqueReqID: type,
				queryParams: {
					...params,
					role,
				},
				onSuccess: [
					(response) => () => {
						const data = response.body.data;
						if (!data || !Array.isArray(data)) return;
						dispatch(append === appendMode.APPEND ? appendCampaigns(type, data) : setCampaigns(type, data));

						if (callGtmFilter) {
							pushToFilterDataLayer({
								filterSection: 'Kampaně',
								formData,
								formDataFieldsMap,
							});
						}
					},
				],
			}),
		);
	};

export const fetchCampaign = (id) => (dispatch) => {
	return dispatch(
		fetch.actions.get(fetch.constants.urls.campaignDetail, {
			replaceParams: { id },
			onSuccess: [
				(response) => () => {
					const campaign = response.body;
					dispatch(appendCampaigns(CURRENT_CAMPAIGNS, [campaign]));
				},
			],
		}),
	);
};

export const fetchCurrentCampaigns = fetchCampaigns(CURRENT_CAMPAIGNS);
export const fetchPlanedCampaigns = fetchCampaigns(PLANED_CAMPAIGNS);
export const currentCampaignActions = generateCommonFetchActions(fetchCurrentCampaigns, currentCampaignsUrl);
export const planedCampaignsActions = generateCommonFetchActions(fetchPlanedCampaigns, planedCampaignsUrl);

export const sortRecords = (sort) => (dispatch) => {
	// This is hack, adding id sort because database is otherwise removing records.
	const sortWithId = [sort, 'id,desc'];
	dispatch(setSort(sortWithId));
	dispatch(currentCampaignActions.loadSorted(sortWithId));
};

export const submitForm =
	(initilizingFetch) =>
	async (dispatch, getState, { getFormData }) => {
		const context = appContext.selectors.getAppContext(getState());
		const campaignId = pathOr(null, ['ias', 'campaignId'], context);
		const mode = campaignId ? appendMode.APPEND : appendMode.SET;
		const data = await getFormData(CURRENT_CAMPAIGNS_FORM_NAME);
		const formData = trimFormData(data);
		const sort = getSort(getState());
		const channelIds = user.selectors.getChannelIds(getState());

		if (initilizingFetch) {
			// initilizing the campaigns screen, so we fetch both types of campaigns
			dispatch(
				fetchCurrentCampaigns(
					{
						queryParams: queryParamsFactory(CURRENT_CAMPAIGNS, formData, sort),
						callGtmFilter: true,
						formData,
					},
					mode,
				),
			);
			dispatch(
				fetchPlanedCampaigns(
					{ queryParams: queryParamsFactory(PLANED_CAMPAIGNS, formData, sort, channelIds) },
					mode,
				),
			);
		} else {
			// not an initilizing fetch but specific filter submit fetch - relates only to current campaigns
			dispatch(
				fetchCurrentCampaigns(
					{
						queryParams: queryParamsFactory(CURRENT_CAMPAIGNS, formData, sort),
						callGtmFilter: true,
						formData,
					},
					mode,
				),
			);
		}
	};

export const resetCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(reset());
		dispatch(setVisibleState({ id: subheaders.currentCampaigns, visible: false }));
		sendControlNext(CURRENT_CAMPAIGNS_FORM_NAME, resetForm());
		// true as the "initilizingFetch" parameter - reload to default state
		dispatch(submitForm(true));
	};
